// The facts recorded against this deal, and who said so.
//
// A fact is a fact whether a machine measured it or a person typed it, and the
// one thing a reader has to be able to see is WHICH. `source` is therefore a
// column and not a footnote: "a person typed 412 minutes" and "the deployment
// backend measured 412 minutes" are different claims about the same number, and
// a screen that showed only the number would make them look alike.
//
// A FACT HAS NO PATCH. A correction is a re-ingest of the same key, which is
// what makes a person's write and a feed's write one operation — so the only
// action here is Delete, for a row that should never have existed at all.
//
// Read per DEAL rather than per line, so a fact recorded for a period no clause
// covers is visible. That is a real situation — somebody files a reading against
// a month the contract does not bill — and it would be invisible nested under
// each line.

import type { ReactNode } from "react";
import type { DealFact, DealListItem } from "@crm/shared";
import styles from "./DealFactsSection.module.css";
import { Badge } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card, CardHeader } from "../../design/Card.js";
import { EmptyRow, Table, Td, Th } from "../../design/Table.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { useDealFacts } from "../../api/queries.js";
import { formatInstantDate, formatMonth } from "../../format/calendarDate.js";
import { formatMoney, formatQuantity } from "../../format/money.js";
import { factSourceLabel } from "../../format/labels.js";

export interface DealFactsSectionProps {
  deal: DealListItem;
  canEdit: boolean;
  onAdd: () => void;
  onDelete: (fact: DealFact) => void;
}

export function DealFactsSection({
  deal,
  canEdit,
  onAdd,
  onDelete,
}: DealFactsSectionProps): ReactNode {
  const facts = useDealFacts(deal.id);

  return (
    <Card>
      <CardHeader
        title="Recorded facts"
        description="Every figure this deal's clauses read that no platform measures for us — a meter reading, a partner's royalty report, an infrastructure invoice, a milestone that landed."
        actions={
          canEdit ? (
            <Button variant="primary" small onClick={onAdd}>
              <span aria-hidden="true">+</span>
              Record a fact
            </Button>
          ) : undefined
        }
      />
      <QueryBoundary query={facts} label="the facts recorded against this deal">
        {(page) => (
          <Table label={`Facts recorded against ${deal.customer_name}`}>
            <thead>
              <tr>
                <Th>Period</Th>
                <Th>What it says</Th>
                <Th>Who said so</Th>
                <Th>Recorded</Th>
                {canEdit && <Th />}
              </tr>
            </thead>
            <tbody>
              {page.items.length === 0 ? (
                <EmptyRow
                  colSpan={canEdit ? 5 : 4}
                  title="No facts recorded"
                  description="Every figure on this deal comes from the contract or from a measured feed. A clause that reads a fact and finds none reports so on the statement."
                />
              ) : (
                page.items.map((fact) => (
                  <tr key={fact.id}>
                    <Td mono nowrap>
                      {formatMonth(fact.period)}
                    </Td>
                    <Td strong>
                      {factValue(fact, deal.currency)}
                      {fact.note !== null && <span className={styles.note}>{fact.note}</span>}
                    </Td>
                    <Td>
                      <Badge shape="tag" tone={fact.source === "person" ? "brand" : "slate"}>
                        {factSourceLabel(fact.source)}
                      </Badge>
                    </Td>
                    <Td muted nowrap>
                      {formatInstantDate(fact.received_at)}
                      <span className={styles.by}>{fact.recorded_by}</span>
                    </Td>
                    {canEdit && (
                      <Td>
                        <div className={styles.rowActions}>
                          <Button
                            small
                            variant="subtle"
                            onClick={() => onDelete(fact)}
                            aria-label={`Delete the ${fact.kind} fact for ${fact.period}`}
                          >
                            Delete
                          </Button>
                        </div>
                      </Td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </QueryBoundary>
    </Card>
  );
}

/** What one fact states, in the words of its kind. */
function factValue(fact: DealFact, dealCurrency: DealListItem["currency"]): string {
  if (fact.amount_minor !== null) {
    return formatMoney(fact.amount_minor, fact.currency ?? dealCurrency);
  }
  if (fact.quantity !== null) return `${formatQuantity(fact.quantity)} units`;
  if (fact.milestone_seq !== null) return `Milestone ${String(fact.milestone_seq)} landed`;
  return "—";
}
